/**
 * 掲示板 投稿詳細画面 (v3.5 Board / Phase 2)。
 * 仕様書: docs/02_work/done/plan_board_feature.md §1-4
 *
 * URL: /board/:postId
 *
 * Phase 2 スコープ:
 * - per-user 状態 chip + 未回答者の明示 (宛先指定時のみ)
 * - あなたのアクション (タイプ別ボタン。自分が宛先 && 未クローズのときのみ)
 * - ケースをクローズ (投稿者 or 宛先者。クローズ済みは灰バナー表示)
 * - 詳細表示時の冪等な既読化 (markBoardPostViewed。useEffect からのみ呼ぶ —
 *   onSnapshot コールバック内での呼び出し禁止の家訓)
 * - スレッド / メディアは Phase 3、通知は Phase 5
 */
import type { BoardRecipientState } from '@goenhub/shared';
import {
  BOARD_POST_TYPE_LABEL,
  BOARD_RECIPIENT_STATE_LABEL,
  BOARD_TYPE_ACTIONS,
  BOARD_URGENCY_LABEL,
  canViewBoardPost,
  isBoardPostPendingPublish,
  isBoardPostRestricted,
} from '@goenhub/shared';
import {
  Archive,
  ArchiveRestore,
  CalendarClock,
  Lock,
  MessagesSquare,
  Pencil,
  Pin,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  BOARD_STATE_VARIANT,
  BOARD_TYPE_VARIANT,
  BOARD_URGENCY_VARIANT,
  boardDueChip,
  boardOverallStatus,
} from './boardMeta';
import { BoardRichTextEditor } from './components/BoardRichTextEditor';
import { ThreadPanel } from './components/ThreadPanel';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useFeatureActions, useMyPermissions } from '@/features/permissions';
import { useAdminDirectory } from '@/hooks/useAdminDirectory';
import { useAuth } from '@/hooks/useAuth';
import { useBoardActionReports } from '@/hooks/useBoardActionReports';
import { useBoardCategories } from '@/hooks/useBoardCategories';
import {
  useActOnBoardPost,
  useBoardPost,
  useCloseBoardPost,
  useDeleteBoardPost,
  useMarkBoardPostViewed,
  usePublishBoardPostNow,
  useReopenBoardPost,
} from '@/hooks/useBoardPosts';
import { formatDate, toDate } from '@/lib/firestoreTime';
import { BoardAttachmentSection } from '@/pages/board/components/BoardAttachmentSection';
import { BoardReferenceList } from '@/pages/board/components/BoardReferenceList';
import { BoardSubmissionList } from '@/pages/board/components/BoardSubmissionList';
import { paths } from '@/routes/paths';

/** リッチテキストが実質空か判定する（TipTap は空でも <p></p> を返す）。 */
function htmlHasContent(html: string): boolean {
  return (
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, '')
      .trim() !== ''
  );
}

export function BoardPostDetailPage() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const acts = useFeatureActions('board.posts');
  const { isOwner } = useMyPermissions();
  const post = useBoardPost(postId);
  const categories = useBoardCategories();
  const directory = useAdminDirectory();
  const deletePost = useDeleteBoardPost();
  const actOnPost = useActOnBoardPost();
  const closePost = useCloseBoardPost();
  const reopenPost = useReopenBoardPost();
  const publishNow = usePublishBoardPostNow();
  const { mutate: markViewedMutate } = useMarkBoardPostViewed();

  // スレッドパネルの開閉。一覧の '💬' バッジからの遷移 (?thread=1) では自動オープン。
  const [threadOpen, setThreadOpen] = useState(false);

  // 対応内容（アクション時に一緒に保存する URL / リッチテキスト）
  // 保存済みのときはフォームを畳み、表示セクションの「編集」で開き直す。
  const [reportUrl, setReportUrl] = useState('');
  const [reportHtml, setReportHtml] = useState('');
  const [editingReport, setEditingReport] = useState(false);
  const reportFormRef = useRef<HTMLDivElement>(null);
  const threadAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (threadAutoOpenedRef.current) return;
    if (searchParams.get('thread') === '1') {
      threadAutoOpenedRef.current = true;
      setThreadOpen(true);
    }
  }, [searchParams]);

  const categoryName = useMemo(() => {
    const id = post.data?.categoryId;
    if (!id) return '未分類';
    return categories.data?.find((c) => c.id === id)?.name ?? '未分類';
  }, [post.data?.categoryId, categories.data]);

  /** uid → 表示名 (ディレクトリで解決不可の uid はそのまま表示)。 */
  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of directory.data ?? []) {
      map.set(m.uid, m.fullName || m.displayName || m.uid);
    }
    return (uid: string) => map.get(uid) ?? uid;
  }, [directory.data]);

  const uid = user?.uid ?? null;
  const p = post.data ?? null;
  const isAuthor = Boolean(uid && p && p.authorUid === uid);
  // 編集は投稿者本人のみ。削除は投稿者本人 + owner (2026-07-27。サーバ deleteBoardPost と同条件)
  const canEdit = isAuthor && acts.edit;
  const canDelete = (isAuthor || isOwner) && acts.delete;
  const myEntry = uid && p ? p.recipientStates?.[uid] : undefined;
  const isRecipient = Boolean(myEntry);
  const isClosed = Boolean(p?.closedAt != null);
  // アクション可否は対応内容フォームの表示判定でも使うので早めに確定させる
  const canAct = isRecipient && p?.audience === 'selected' && !isClosed;

  // 既読化 (plan §5-1): 宛先で未確認 or 投稿者で新着アクションあり → postId ごとに1回だけ呼ぶ。
  // useEffect からのみ (onSnapshot コールバック内での書込は家訓で禁止)。
  const markedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!p || !uid || !postId) return;
    const needRecipientRead = p.recipientStates?.[uid]?.state === 'unread';
    const needAuthorClear = p.authorUid === uid && p.authorUnseenActivity;
    if (!needRecipientRead && !needAuthorClear) return;
    if (markedRef.current.has(postId)) return;
    markedRef.current.add(postId);
    markViewedMutate(postId, {
      onError: (err) => console.warn('[BoardPostDetailPage] markViewed failed:', err),
    });
  }, [p, uid, postId, markViewedMutate]);

  /** 予約を待たず今すぐ公開する（= 公開と同時に通知が飛ぶので確認を挟む）。 */
  const handlePublishNow = async () => {
    if (!postId) return;
    if (!window.confirm('予約を取り消して今すぐ公開します。宛先へ通知が送信されます。')) {
      return;
    }
    try {
      await publishNow.mutateAsync(postId);
      toast.success('投稿を公開しました');
    } catch (err) {
      toast.error(`公開失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDelete = async () => {
    if (!post.data || !postId) return;
    // owner が他人の投稿を削除する場合は投稿者名を出して誤操作を防ぐ
    const otherAuthor = !isAuthor ? `${post.data.authorName} さんの` : '';
    if (
      !window.confirm(
        `${otherAuthor}「${post.data.title}」を完全に削除します。この操作は取り消せません。`,
      )
    )
      return;
    try {
      await deletePost.mutateAsync(postId);
      toast.success('投稿を削除しました');
      navigate(paths.board);
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // 自分が保存済みの対応内容。あれば入力フォームは畳んでおく。
  const actionReports = useBoardActionReports(postId);
  const myReport = actionReports.data?.find((r) => r.uid === uid);
  const hasSavedReport = Boolean(myReport && (myReport.url || htmlHasContent(myReport.bodyHtml)));
  /**
   * 入力フォームを出すか（未保存 or 編集中のときだけ）。
   * 読込中に出すと保存済みでも一瞬フォームが見えてしまうので待つ。
   */
  const showReportForm = Boolean(
    canAct && postId && !actionReports.isLoading && (!hasSavedReport || editingReport),
  );

  /** 表示セクションの「編集」→ 保存済みの値を読み込んでフォームを開く。 */
  const handleEditReport = () => {
    setReportUrl(myReport?.url ?? '');
    setReportHtml(myReport?.bodyHtml ?? '');
    setEditingReport(true);
    // フォームは「あなたのアクション」の下にあるので、開いた場所まで送る
    window.setTimeout(
      () => reportFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
      50,
    );
  };

  const handleAct = async (state: BoardRecipientState) => {
    if (!postId || !myEntry) return;
    // フォームが出ている間は「対応内容だけ更新」もあり得るので no-op にしない
    if (myEntry.state === state && !showReportForm) return;
    try {
      await actOnPost.mutateAsync({
        postId,
        state,
        // フォームを出しているときだけ送る。空で送ると対応内容の削除になる（サーバ契約）。
        report: showReportForm
          ? {
              url: reportUrl.trim(),
              bodyHtml: htmlHasContent(reportHtml) ? reportHtml : '',
            }
          : undefined,
      });
      setEditingReport(false);
      toast.success(`「${BOARD_RECIPIENT_STATE_LABEL[state]}」にしました`);
    } catch (err) {
      toast.error(`更新失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleClose = async () => {
    if (!postId || !p) return;
    if (
      !window.confirm(
        'この投稿をクローズします。未回答の宛先もバッジ・リマインダー対象から外れます。よろしいですか？',
      )
    )
      return;
    try {
      await closePost.mutateAsync(postId);
      toast.success('投稿をクローズしました');
    } catch (err) {
      toast.error(`クローズ失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  /**
   * 再オープン（投稿者本人のみ）。
   * 宛先へ通知が飛び、未対応の宛先がバッジ・期日リマインダーへ戻るので確認を挟む。
   */
  const handleReopen = async () => {
    if (!postId || !p) return;
    const targets =
      p.audience === 'selected' ? `宛先 ${(p.recipientUids ?? []).length} 名` : '全員';
    if (
      !window.confirm(
        `この投稿を再オープンします。${targets}に再オープンの通知が送信され、未対応の宛先はバッジ・期日リマインダーの対象に戻ります。よろしいですか？`,
      )
    )
      return;
    try {
      await reopenPost.mutateAsync(postId);
      toast.success('投稿を再オープンしました');
    } catch (err) {
      toast.error(`再オープン失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (post.isLoading) {
    return (
      <Screen title="投稿詳細" backTo={paths.board}>
        <div className="flex items-center justify-center p-12">
          <Spinner />
        </div>
      </Screen>
    );
  }

  if (!p) {
    return (
      <Screen title="投稿詳細" backTo={paths.board}>
        <EmptyState
          icon={Pin}
          title="投稿が見つかりません"
          description="削除されたか、URL が誤っている可能性があります。"
        />
      </Screen>
    );
  }

  // 閲覧制限 ON で宛先でも投稿者でもない (URL 直打ち等)。一覧と同じ判定で内容を出さない。
  if (!canViewBoardPost(p, uid)) {
    return (
      <Screen title="投稿詳細" backTo={paths.board}>
        <EmptyState
          icon={Lock}
          title="この投稿は閲覧できません"
          description="閲覧制限が設定されており、宛先に指定された人と投稿者本人のみ閲覧できます。"
        />
      </Screen>
    );
  }

  const restricted = isBoardPostRestricted(p);

  const dueChip = p.dueDate ? boardDueChip(p.dueDate) : null;
  const overall = boardOverallStatus(p);
  const openUids = p.openRecipientUids ?? [];
  const canClose = (isAuthor || isRecipient) && !isClosed;

  // 保存済みの対応内容を編集して単独で保存するときのステータス。
  // actOnBoardPost は必ず state を要求するため、現在のステータス（タイプ別の
  // 許可アクションに含まれるもの）をそのまま送って内容だけ差し替える。
  const allowedActions = BOARD_TYPE_ACTIONS[p.type] ?? [];
  const reportSaveState: BoardRecipientState | null =
    myEntry && allowedActions.includes(myEntry.state) ? myEntry.state : (myReport?.state ?? null);

  // スレッド新着 (§1-5): threadCount - threadSeen[me]。参加者 (投稿者 or 宛先) のみ表示。
  const threadCount = p.threadCount ?? 0;
  const isParticipant = isAuthor || isRecipient;
  const threadSeen = uid ? (p.threadSeen?.[uid] ?? 0) : 0;
  const threadUnread = isParticipant ? Math.max(0, threadCount - threadSeen) : 0;

  // 公開予約中 (未公開)。宛先には見えず通知も飛んでいない状態。
  const pendingPublish = isBoardPostPendingPublish(p);
  const publishAtDate = pendingPublish ? toDate(p.publishAt) : null;
  const publishAtLabel = publishAtDate
    ? publishAtDate.toLocaleString('ja-JP', {
        month: 'long',
        day: 'numeric',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : null;

  const closedDate = toDate(p.closedAt);
  const closedLabel = isClosed
    ? `この投稿はクローズされています（${
        closedDate ? `${closedDate.getMonth() + 1}/${closedDate.getDate()}` : '—'
      }${p.closedBy ? ` · ${nameOf(p.closedBy)}` : ''}）`
    : null;

  /** アクションボタンのスタイル: 現在状態は保留=purple / 終端=green でハイライト。 */
  const actionBtnClass = (state: BoardRecipientState) => {
    const active = myEntry?.state === state;
    const base = 'px-3 py-1.5 rounded-sm text-xs border transition-colors ';
    if (!active) {
      return (
        base +
        'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]'
      );
    }
    if (state === 'onhold') {
      return (
        base +
        'border-[var(--accent-purple)] bg-[var(--accent-purple-subtle)] text-[var(--accent-purple)]'
      );
    }
    return (
      base +
      'border-[var(--accent-green)] bg-[var(--accent-green-subtle)] text-[var(--accent-green)]'
    );
  };

  return (
    <Screen
      title="投稿詳細"
      backTo={paths.board}
      actions={
        canEdit || canDelete ? (
          <>
            {canEdit && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(paths.boardPostEdit.replace(':postId', p.id))}
              >
                <Pencil className="size-4 mr-1" />
                編集
              </Button>
            )}
            {canDelete && (
              <Button
                type="button"
                variant="danger"
                onClick={() => void handleDelete()}
                disabled={deletePost.isPending}
              >
                <Trash2 className="size-4 mr-1" />
                削除
              </Button>
            )}
          </>
        ) : undefined
      }
    >
      <div className="max-w-3xl space-y-4">
        {/* 公開予約バナー (投稿者本人にしか見えない状態であることを明示する) */}
        {pendingPublish && (
          <div className="rounded-md border border-[var(--accent-amber)] bg-[var(--accent-amber-subtle)] px-3 py-2.5 flex items-center gap-2 flex-wrap">
            <CalendarClock className="size-4 flex-shrink-0 text-[var(--accent-amber)]" />
            <span className="text-xs text-[var(--text-primary)]">
              <span className="font-semibold">{publishAtLabel ?? '指定日時'}</span>{' '}
              に公開されます。公開までは自分だけが閲覧でき、通知も送信されません。
            </span>
            {isAuthor && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="ml-auto"
                onClick={() => void handlePublishNow()}
                disabled={publishNow.isPending}
              >
                {publishNow.isPending ? '公開中...' : '今すぐ公開'}
              </Button>
            )}
          </div>
        )}

        {/* 閲覧制限バナー (2026-09-07): 宛先と投稿者本人だけに見えている状態を明示する */}
        {restricted && (
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-hover)] px-3 py-2 text-xs text-[var(--text-secondary)] flex items-center gap-2">
            <Lock className="size-4 flex-shrink-0" />
            <span>閲覧制限あり: この投稿は宛先に指定された人と投稿者本人だけが閲覧できます。</span>
          </div>
        )}

        {/* クローズ済みバナー。再オープンは投稿者本人のみ（2026-08-06）。 */}
        {closedLabel && (
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-hover)] px-3 py-2 text-xs text-[var(--text-muted)] flex items-center gap-2">
            <Archive className="size-4 flex-shrink-0" />
            {/* closedBy が名前解決できず uid 表示になると長くなるので truncate で
                ボタンを押し出さないようにする。 */}
            <span className="min-w-0 truncate">{closedLabel}</span>
            {isAuthor && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="ml-auto flex-shrink-0"
                onClick={() => void handleReopen()}
                disabled={reopenPost.isPending}
              >
                <ArchiveRestore className="size-3.5" />
                {reopenPost.isPending ? '再オープン中…' : '再オープン'}
              </Button>
            )}
          </div>
        )}

        <div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Badge variant={BOARD_TYPE_VARIANT[p.type]}>{BOARD_POST_TYPE_LABEL[p.type]}</Badge>
            <Badge variant={BOARD_URGENCY_VARIANT[p.urgency]}>
              緊急度: {BOARD_URGENCY_LABEL[p.urgency]}
            </Badge>
            {overall && <Badge variant={overall.variant}>{overall.label}</Badge>}
            {dueChip && <Badge variant={dueChip.variant}>{dueChip.label}</Badge>}
          </div>
          <h1
            className={
              'text-xl font-bold ' +
              (isClosed ? 'text-[var(--text-muted)]' : 'text-[var(--text-primary)]')
            }
          >
            {p.title}
          </h1>
          <div className="text-xs text-[var(--text-muted)] mt-2 flex items-center gap-3 flex-wrap">
            <span>投稿者: {p.authorName}</span>
            <span>作成: {formatDate(p.createdAt)}</span>
            <span>カテゴリ: {categoryName}</span>
            <span>期日: {p.dueDate ?? '無期日'}</span>
          </div>

          {/* スレッドを開くボタン (§1-5)。新着があればアクセントバッジを表示。 */}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setThreadOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs border border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)] transition-colors"
            >
              <MessagesSquare className="size-4" />
              🧵 スレッド（{threadCount}）
              {threadUnread > 0 && (
                <span className="ml-0.5 inline-flex items-center rounded-full bg-[var(--accent)] px-1.5 py-px text-[10px] font-semibold text-white tabular-nums">
                  新着 {threadUnread}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* 宛先とステータス */}
        <div className="py-3 border-y border-[var(--border)] space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[var(--text-muted)] flex-shrink-0">
              宛先とステータス:
            </span>
            {p.audience !== 'selected' ? (
              <Badge variant="default">全員宛 — ステータス追跡なし</Badge>
            ) : (
              (p.recipientUids ?? []).map((ruid) => {
                const entry = p.recipientStates?.[ruid];
                const name = nameOf(ruid);
                return (
                  <span
                    key={ruid}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] pl-1 pr-1.5 py-0.5"
                  >
                    <span className="size-5 rounded-full bg-[var(--bg-hover)] flex items-center justify-center text-[10px] text-[var(--text-secondary)] flex-shrink-0">
                      {name.charAt(0)}
                    </span>
                    <span className="text-xs text-[var(--text-primary)]">{name}</span>
                    {entry && (
                      <Badge variant={BOARD_STATE_VARIANT[entry.state]}>
                        {BOARD_RECIPIENT_STATE_LABEL[entry.state]}
                      </Badge>
                    )}
                  </span>
                );
              })
            )}
          </div>
          {/* 未回答者の明示 (Q4 確定仕様) */}
          {p.audience === 'selected' && !isClosed && openUids.length > 0 && (
            <p className="text-xs text-[var(--accent-amber)]">
              未回答: {openUids.map((u) => nameOf(u)).join('、')}（残り{openUids.length}名）
            </p>
          )}
        </div>

        {/* 本文 (TipTap HTML。管理者作成 HTML のみの既存信頼モデル) */}
        <div
          className="board-body prose prose-sm dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: p.bodyHtml }}
        />

        {/* 参考ファイル（投稿者が付けた資料）。宛先指定に関係なく全員が閲覧・DLできる。 */}
        {postId && <BoardReferenceList postId={postId} />}

        {/* 対応内容 + 成果物（本文の下で確認できるようにまとめて表示）。
            自分のカードには「編集」ボタンが出て、入力フォームへ切り替わる。 */}
        {postId && (
          <BoardSubmissionList
            postId={postId}
            myUid={uid}
            canEdit={canAct}
            onEdit={handleEditReport}
            editing={editingReport}
          />
        )}

        {/* あなたのアクション (自分が宛先 && 宛先指定 && 未クローズ) */}
        {canAct && (
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-[var(--text-secondary)] flex-shrink-0">
              あなたのアクション:
            </span>
            {BOARD_TYPE_ACTIONS[p.type].map((state) => (
              <button
                key={state}
                type="button"
                onClick={() => void handleAct(state)}
                disabled={actOnPost.isPending}
                className={actionBtnClass(state)}
              >
                {BOARD_RECIPIENT_STATE_LABEL[state]}
              </button>
            ))}
            {myEntry && myEntry.state !== 'unread' && myEntry.state !== 'read' && (
              <span className="text-[11px] text-[var(--text-muted)]">
                現在: {BOARD_RECIPIENT_STATE_LABEL[myEntry.state]}（別のボタンで変更できます）
              </span>
            )}
          </div>
        )}

        {/* 対応内容（URL / リッチテキスト）。
            未保存のときはアクションボタンで一緒に保存。
            保存済みは畳んでおき、上の表示セクションの「編集」で開く。 */}
        {showReportForm && postId && (
          <div
            ref={reportFormRef}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 space-y-3"
          >
            <span className="text-xs font-medium text-[var(--text-secondary)]">
              対応内容
              <span className="text-[var(--text-muted)]">
                {editingReport ? '（編集中）' : '（任意）'}
              </span>
            </span>
            <div className="space-y-1">
              <Label htmlFor="report-url">URL</Label>
              <Input
                id="report-url"
                type="url"
                value={reportUrl}
                onChange={(e) => setReportUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-1">
              <Label>テキスト</Label>
              <BoardRichTextEditor
                value={reportHtml}
                onChange={setReportHtml}
                postId={postId}
                placeholder="対応内容や補足を入力..."
                minHeight={140}
              />
            </div>
            {editingReport ? (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => reportSaveState && void handleAct(reportSaveState)}
                  disabled={actOnPost.isPending || !reportSaveState}
                >
                  {actOnPost.isPending ? '保存中…' : '保存'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditingReport(false)}
                  disabled={actOnPost.isPending}
                >
                  キャンセル
                </Button>
                <span className="text-[11px] text-[var(--text-muted)]">
                  ステータス（
                  {reportSaveState ? BOARD_RECIPIENT_STATE_LABEL[reportSaveState] : '—'}
                  ）は変わりません。空にして保存すると対応内容は削除されます。
                </span>
              </div>
            ) : (
              <p className="text-[11px] text-[var(--text-muted)]">
                上の「{allowedActions.map((s) => BOARD_RECIPIENT_STATE_LABEL[s]).join(' / ')}
                」を押すと、ステータスと一緒に保存されます。
              </p>
            )}
          </div>
        )}

        {/* 添付ファイル (成果物)。宛先指定の投稿でのみ扱う。
            アップロードは宛先者のみ、閲覧・ダウンロードは社内メンバー全員。 */}
        {p.audience === 'selected' && postId && (
          <BoardAttachmentSection
            postId={postId}
            canUpload={canAct}
            myState={myEntry?.state}
            myUid={uid}
          />
        )}

        {/* ケースをクローズ */}
        {canClose && (
          <div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void handleClose()}
              disabled={closePost.isPending}
            >
              <Archive className="size-4 mr-1" />
              ケースをクローズ
            </Button>
          </div>
        )}
      </div>

      {/* スレッドパネル (Phase 3) */}
      <ThreadPanel postId={p.id} open={threadOpen} onOpenChange={setThreadOpen} />
    </Screen>
  );
}
